import React from "react";
import * as palette from "@govlaunch/palette";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import mediaQueries from "~/components/mediaQueries";
import { RelatedUrlCard } from "~/components/newsworthy/RelatedUrlCard";
import { Margin } from "~/components/spacings";
import ProfileBox from "~/components/profile/ProfileBox";
import StarIcon from "~/components/icons/StarIcon";
import Resources from "~/components/profile/Resources";
import { IFlatResource } from "~/types/types";
import { FormattedMessage } from "react-intl";

interface IVendorHighlights {
  vendor: NonNullable<IVendorQuery["company"]>;
}

export default function VendorHighlights({ vendor }: IVendorHighlights) {
  const resourcesExist = vendor.resources && vendor.resources.length > 0;

  if (vendor.highlights.length === 0 && vendor.newsworthy.length === 0 && !resourcesExist) {
    return (
      <ProfileBox
        css={{
          padding: 26,
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: palette.white,
            color: palette.mediumGray,
          }}
        >
          <StarIcon />
          <Margin ml={14}>
            <span
              css={{
                fontSize: 18,
                fontWeight: 500,
              }}
            >
              <FormattedMessage defaultMessage="No highlights" id="vma3lC" />
            </span>
          </Margin>
        </div>
      </ProfileBox>
    );
  }

  return (
    <div>
      {vendor.highlights && vendor.highlights.length > 0 && (
        <Margin mb={24}>
          <div
            css={{
              height: "100%",
              backgroundColor: palette.white,
              border: `1px solid ${palette.lightestGray}`,
              borderRadius: 5,
            }}
          >
            <div
              css={{
                padding: 16,
                textTransform: "uppercase",
                fontSize: 12,
                color: palette.darkGray,
                letterSpacing: 0.5,
                fontWeight: 500,
                borderBottom: `solid 1px ${palette.lightestGray}`,
              }}
            >
              <FormattedMessage defaultMessage="Highlights" id="KGmQjH" />
            </div>

            <div css={{ padding: 16 }}>
              <ul css={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridRowGap: 5 }}>
                {(vendor.highlights || []).map((highlight, index) => (
                  <li
                    key={index}
                    css={{
                      margin: 0,
                      padding: 0,
                      fontSize: 15,
                      color: palette.text,
                      lineHeight: 1.5,
                      verticalAlign: "middle",
                      display: "flex",
                      alignItems: "baseline",
                    }}
                  >
                    <div
                      css={{
                        display: "inline-block",
                        paddingRight: 20,
                        marginRight: 0,
                        width: 13,
                        textAlign: "center",
                      }}
                    >
                      <span
                        css={{
                          backgroundColor: palette.primary,
                          width: 10,
                          height: 10,
                          display: "inline-block",
                          borderRadius: "100%",
                        }}
                      />
                    </div>

                    <span
                      css={{
                        wordBreak: "break-word",
                      }}
                    >
                      {highlight}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Margin>
      )}

      {vendor.newsworthy.length > 0 && (
        <div>
          <h1
            css={{
              fontSize: 16,
              color: palette.darkGray,
              fontWeight: "normal",
            }}
          >
            <FormattedMessage defaultMessage="Newsworthy" id="JhItYg" />
          </h1>
          <div
            css={mediaQueries({
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
              gridColumnGap: 16,
              gridRowGap: 16,
            })}
          >
            {vendor.newsworthy.map((newsworthyItem) => (
              <RelatedUrlCard
                description={newsworthyItem.description || ""}
                title={newsworthyItem.title}
                url={newsworthyItem.url}
                siteName={newsworthyItem.siteName}
                favicon={newsworthyItem.favicon || ""}
                key={newsworthyItem.url}
              />
            ))}
          </div>
        </div>
      )}

      {vendor.resources && vendor.resources.length > 0 && (
        <Resources resources={vendor.resources as IFlatResource[]} titleColor={palette.darkGray} />
      )}
    </div>
  );
}
